import React, { useState, useEffect } from 'react';
import { submitCreatorRequest, getMyCreatorRequest } from '../api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FileVideo, PlayCircle, Globe, Loader, CheckCircle, Clock, XCircle } from 'lucide-react';

export const BecomeCreatorPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingRequest, setExistingRequest] = useState(null);
  const [formData, setFormData] = useState({
    motivation: '',
    content_type: '',
    portfolio_url: '',
    youtube_channel: '',
    facebook_page: '',
    phone_number: ''
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    if (!authLoading && user) {
      checkExistingRequest();
    }
  }, [user, authLoading]);

  const checkExistingRequest = async () => {
    try {
      const response = await getMyCreatorRequest();
      if (response.data.request) {
        setExistingRequest(response.data.request);
      }
    } catch (error) {
      console.error('Failed to check request:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        motivation: formData.motivation,
        content_type: formData.content_type,
      };
      if (formData.portfolio_url && formData.portfolio_url.trim()) {
        payload.portfolio_url = formData.portfolio_url.trim();
      }
      if (formData.youtube_channel && formData.youtube_channel.trim()) {
        payload.youtube_channel = formData.youtube_channel.trim();
      }
      if (formData.facebook_page && formData.facebook_page.trim()) {
        payload.facebook_page = formData.facebook_page.trim();
      }
      if (formData.phone_number && formData.phone_number.trim()) {
        payload.phone_number = formData.phone_number.trim();
      }
      await submitCreatorRequest(payload);
      alert('Demande soumise avec succès! Vous serez notifié de la décision.');
      setFormData({ motivation: '', content_type: '', portfolio_url: '' });
      await checkExistingRequest();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const statusInfo = {
    pending: { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-500/10 border-yellow-500/30', label: '⏳ En attente de validation' },
    approved: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-500/10 border-green-500/30', label: '✅ Demande approuvée' },
    rejected: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-500/10 border-red-500/30', label: '❌ Demande rejetée' },
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Déjà créateur ou superadmin
  if (user && (user.role === 'creator' || user.role === 'superadmin')) {
    return (
      <div className="min-h-screen bg-background pt-20 pb-12">
        <div className="container max-w-2xl mx-auto px-4">
          <div className="bg-card rounded-lg border border-border p-8 text-center">
            <div className="text-6xl mb-4">🎬</div>
            <h1 className="text-2xl font-bold mb-2 text-foreground">Vous êtes déjà créateur !</h1>
            <p className="text-muted-foreground mb-6">Votre compte a accès au Studio Créateur YetuTv. Commencez à partager vos créations avec la communauté.</p>
            <button
              onClick={() => navigate('/creator/studio')}
              className="bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors"
            >
              Accéder au Studio Créateur
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Demande en attente ou approuvée → afficher statut
  if (existingRequest && (existingRequest.status === 'pending' || existingRequest.status === 'approved')) {
    const getStatusColor = (status) => {
      switch (status) {
        case 'approved': return 'text-green-500';
        case 'rejected': return 'text-red-500';
        default: return 'text-yellow-500';
      }
    };

    const getStatusText = (status) => {
      switch (status) {
        case 'approved': return '✅ Approuvée';
        case 'rejected': return '❌ Rejetée';
        default: return '⏳ En attente';
      }
    };

    return (
      <div className="min-h-screen bg-background pt-20 pb-12">
        <div className="container max-w-2xl mx-auto px-4">
          <div className="bg-card rounded-lg border border-border p-8">
            <h1 className="text-3xl font-bold mb-6 text-foreground">
              Votre Demande Créateur
            </h1>

            <div className="space-y-6">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Statut</p>
                <p className={`text-xl font-bold ${getStatusColor(existingRequest.status)}`}>
                  {getStatusText(existingRequest.status)}
                </p>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-1">Type de contenu</p>
                <p className="text-foreground">{existingRequest.content_type}</p>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-1">Motivation</p>
                <p className="text-foreground whitespace-pre-wrap">{existingRequest.motivation}</p>
              </div>

              {existingRequest.portfolio_url && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Portfolio</p>
                  <a 
                    href={existingRequest.portfolio_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {existingRequest.portfolio_url}
                  </a>
                </div>
              )}

              {existingRequest.admin_notes && (
                <div className="bg-accent p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Note de l'administrateur</p>
                  <p className="text-foreground">{existingRequest.admin_notes}</p>
                </div>
              )}

              {existingRequest.status === 'approved' && (
                <button
                  onClick={() => navigate('/creator/studio')}
                  className="w-full bg-primary text-primary-foreground py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors"
                >
                  🎬 Accéder au Studio Créateur
                </button>
              )}

            </div>
          </div>
        </div>
      </div>
    );
  }

  // Formulaire de demande (nouveau ou après rejet)
  const isResubmit = existingRequest && existingRequest.status === 'rejected';
  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="container max-w-2xl mx-auto px-4">
        <div className="bg-card rounded-lg border border-border p-8">

          {/* Bannière rejet */}
          {isResubmit && (
            <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <p className="text-red-500 font-medium text-sm">❌ Votre précédente demande a été rejetée.</p>
              {existingRequest.admin_notes && (
                <p className="text-muted-foreground text-sm mt-1">Note admin : {existingRequest.admin_notes}</p>
              )}
              <p className="text-muted-foreground text-sm mt-2">Vous pouvez soumettre une nouvelle demande ci-dessous.</p>
            </div>
          )}

          {/* Hero */}
          <div className="flex items-center gap-4 mb-8 pb-6 border-b border-border">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
              <FileVideo className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {isResubmit ? 'Nouvelle demande Créateur' : 'Devenir Créateur YetuTv'}
              </h1>
              <p className="text-muted-foreground text-sm mt-1">
                Partagez vos créations avec la communauté congolaise et monétisez votre contenu.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Type de contenu */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Type de contenu que vous souhaitez créer *
              </label>
              <select
                required
                value={formData.content_type}
                onChange={(e) => setFormData({ ...formData, content_type: e.target.value })}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="content-type-select"
              >
                <option value="">Sélectionner...</option>
                <option value="Vidéos">Vidéos</option>
                <option value="Films">Films</option>
                <option value="Séries">Séries / Émissions TV</option>
                <option value="Photos">Photos / Photographie</option>
                <option value="Musique">Musique / Clips</option>
                <option value="Éducation">Éducation</option>
                <option value="Sport">Sport</option>
                <option value="Autre">Autre</option>
              </select>
            </div>

            {/* Motivation */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Pourquoi voulez-vous devenir créateur sur YetuTv? *
              </label>
              <textarea
                required
                rows={6}
                value={formData.motivation}
                onChange={(e) => setFormData({ ...formData, motivation: e.target.value })}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                placeholder="Expliquez votre motivation, votre expérience, et le type de contenu que vous souhaitez partager..."
                data-testid="motivation-textarea"
              />
            </div>

            {/* Numéro de téléphone */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Numéro de téléphone (pour être contacté)
              </label>
              <input
                type="tel"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="+243 8XX XXX XXX"
              />
            </div>

            {/* Channels existants */}
            <div className="border border-border rounded-xl p-5 space-y-4">
              <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Globe className="w-4 h-4 text-primary" />
                Vos chaînes / pages existantes (optionnel mais recommandé)
              </p>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
                  <PlayCircle className="w-3.5 h-3.5 text-red-500" /> Chaîne YouTube
                </label>
                <input
                  type="url"
                  value={formData.youtube_channel}
                  onChange={(e) => setFormData({ ...formData, youtube_channel: e.target.value })}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="https://youtube.com/@votrechaîne"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                  📘 Page Facebook / Instagram
                </label>
                <input
                  type="url"
                  value={formData.facebook_page}
                  onChange={(e) => setFormData({ ...formData, facebook_page: e.target.value })}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="https://facebook.com/votrepage"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                  🔗 Autre lien (portfolio, site web, TikTok…)
                </label>
                <input
                  type="url"
                  value={formData.portfolio_url}
                  onChange={(e) => setFormData({ ...formData, portfolio_url: e.target.value })}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="https://..."
                  data-testid="portfolio-input"
                />
              </div>
            </div>

            {/* Info box */}
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 space-y-2">
              <p className="text-sm font-medium text-foreground">💡 Comment ça marche ?</p>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>Soumettez votre demande avec votre motivation</li>
                <li>Notre équipe examine votre profil sous 48h</li>
                <li>Si approuvé, vous accédez au Studio Créateur</li>
                <li>Publiez vos vidéos et commencez à générer des revenus</li>
              </ul>
            </div>

            {/* Buttons */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="flex-1 bg-secondary text-secondary-foreground py-3 rounded-lg font-medium hover:bg-secondary/80 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-primary text-primary-foreground py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-request-button"
              >
                {submitting ? 'Envoi...' : 'Soumettre ma demande'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
